<template>
  <div class="spinner-wheel">
    <v-img
      class="arrow" :src="require('@/assets/img/games/wheel/arrow.png')" contain
      min-width="50" width="20%" max-width="80" aspect-ratio="1"
    />
    <v-img
      ref="wheel"
      src="@/assets/img/games/spinners/spinner.png"
      :width="size"
      :transition="false"
      aspect-ratio="1"
    >
      <div class="segments" :style="{ background: conicGradient }" />
    </v-img>
  </div>
</template>

<script>
import seededRandom from 'seed-random'
import anime from 'animejs/lib/anime.es'
import { COLOR_HEXCODES, GAME_COLORS } from '../utils'

export default {
  props: {
    size: {
      type: [Number, String],
      default: 64,
    },
    game: {
      type: Object,
      required: true,
    },
    conga: {
      type: Object,
      required: true,
    },
  },
  computed: {
    slots() {
      const { slots } = this.game
      return GAME_COLORS.slice(0, slots)
    },
    players() {
      return this.game.players
    },
    playersByColor() {
      return Object.values(this.players).reduce((acc, player) => {
        acc[player.color] = player
        return acc
      }, {})
    },
    segments() {
      const { slots, playersByColor } = this

      const creatorAmount = this.players[this.game.data.creator].amount

      const potTotal = slots.reduce((acc, color) => acc + (playersByColor[color]?.amount ?? creatorAmount), 0)

      const segments = []

      for (const [i, color] of slots.entries()) {
        const lastSegment = segments[i - 1]
        const player = playersByColor[color]

        const offset = (lastSegment?.offset ?? 0) + (lastSegment?.percent ?? 0)
        const percent = ((player?.amount ?? creatorAmount) / potTotal) * 100

        segments.push({
          color,
          hex: player ? COLOR_HEXCODES[color] : '#00000000',
          offset,
          percent,
        })
      }

      return segments
    },
    conicGradient() {
      const stops = this.segments.map(segment => `${segment.hex} 0 ${segment.offset + segment.percent}%`)
      return `conic-gradient(${stops.join(',')})`
    },
  },
  watch: {
    'game.outcome': function handler(val) {
      if (val) this.roll()
    },
  },
  mounted() {
    if (this.game.outcome) {
      this.roll()
    }
  },
  methods: {
    roll() {
      const { game } = this
      const { data: { maxTickets } } = game

      const config = this.conga.games.spinners

      const rollTime = config.time.roll

      const { $el: $wheel } = this.$refs.wheel

      const rand = seededRandom(game.id)()
      const easingModifier = +(rand * (0.70 - 0.95) + 0.95).toFixed(2)

      const percent = game.outcome / maxTickets
      const outcomeRotation = 360 * percent
      const rotation = 5 * 360 + outcomeRotation

      const timeSinceRoll = new Date(game.rolledAt).getTime() - Date.now()

      const animation = anime({
        targets: $wheel,
        rotate: [0, -rotation],
        duration: rollTime,
        easing: `cubicBezier(0.5, 1, ${easingModifier}, 1)`,
        autoplay: false,
      })

      const seekTime = Math.abs(timeSinceRoll)
      animation.seek(seekTime)

      if (seekTime < rollTime) {
        animation.play()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.arrow {
  position: absolute;
  z-index: 2;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  contain: content;
}

.segments {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  mix-blend-mode: multiply;
  opacity: 0.8;

  .segment {
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    width: var(--size);
    height: calc(var(--size) / 2);
    transform: rotate(calc(var(--start) * 1deg)) translateY(calc(var(--size) / -2));

    &::before {
      position: absolute;
      width: inherit;
      height: inherit;
      border-radius: var(--size) var(--size) 0 0;
      background-color: var(--color);
      content: ' ';
      transform: rotate(calc(var(--end) * 1deg));
      transform-origin: 50% 100%;
    }
  }
}
</style>
